import type { ElementKey } from '~/types/game'

/**
 * Evolving passives (v0.59, per Javi): a creature that stays TRUE to its
 * nature — its base main element is still its highest stat — awakens a
 * passive at level 5, which strengthens at 7 and again at 9. Condition
 * and tier logic live in `creaturePassive()` (utils/battle.ts); the
 * effects hook into speed, chance, fight timers, healing and damage.
 *
 * APOTHEOSIS (v0.91, per Javi): one EXTRA tier past a passive's last —
 * only creatures that learned the 🌌 Apotheosis trained skill reach it,
 * at level 11. Deliberately broken; it's the endgame chase.
 *
 * The five elemental passives below all run L5/L7/L9 + Apotheosis, so
 * theirs is the fourth. Do NOT hard-code that (v2.73, per Javi): the
 * creature-expansion passives run 2, 3 and 4 natural tiers, and every one
 * of them gets an Apotheosis tier on top. Apotheosis is always "the tier
 * after the last one", never "tier 4" — see `passiveTierFor`, which
 * clamps to whatever the passive actually defines.
 */
export interface PassiveDef {
  /** Stable key. The elemental five use their element name; creature
   * passives (v2.80) use their own slug. */
  id: string
  /** Elemental passives only — the nature that awakens it. Creature
   * passives belong to a specific creature and have no element, so
   * consumers that switch on `element` skip them automatically. */
  element?: ElementKey
  name: string
  icon: string
  /** 🎨 Painted badge (v2.23) — shown wherever the passive appears. */
  image: string
  /**
   * The level each NATURAL tier unlocks at (v2.80). The elemental five
   * run [5, 7, 9], which is exactly what the old fixed ladder produced.
   * Creature passives set their own pacing — some bloom early, some
   * wait. Apotheosis lands two levels past the last entry unless
   * `apotheosisAt` pins it elsewhere (v5.32 — Eternal Return waits
   * until 21).
   */
  unlockLevels: number[]
  /** Overrides the last-unlock+2 Apotheosis level (v5.32). */
  apotheosisAt?: number
  /** Player-facing effect text per tier, natural tiers first and the
   * Apotheosis tier LAST. The five elemental passives are
   * [L5, L7, L9, Apotheosis]; custom passives may define fewer or more
   * natural tiers, so read the last entry as Apotheosis — never index 3. */
  tiers: string[]
}

/** Numeric knobs per tier, consumed by the battle hooks. */
export const PASSIVE_VALUES = {
  /** Fire: flat success-chance bonus on missions it joins (not bosses). */
  fireChance: [4, 8, 12, 20],
  /** Water: personal travel-speed multiplier. */
  waterSpeedMult: [1.25, 1.5, 3, 5],
  /** Grass: HP healed when its mission succeeds (T3+ also +1 XP to it).
   * v4.66 (per Javi): T3 was a dead rung at 2 — now 3. */
  grassHeal: [1, 2, 3, 5],
  /** Lightning: ⚔️ Fight points granted while it's on the team (v0.91 —
   * each point is −4% fight time, summed across the team, cap −70%). */
  lightningFight: [6, 11, 15, 20],
  /** Earth: failure damage reduction when it joins - and (v4.16) expiry reduction while it waits at camp. */
  earthReduction: [1, 2, 3, 6],
} as const

export const PASSIVES: Record<ElementKey, PassiveDef> = {
  fire: {
    id: 'fire',
    element: 'fire',
    name: 'Kindled Resolve',
    icon: '🔥',
    image: '/ui/passives/fire.webp',
    unlockLevels: [5, 7, 9],
    tiers: [
      'Missions it joins gain +4% success chance (bosses excepted).',
      'Missions it joins gain +8% success chance (bosses excepted).',
      'Missions it joins gain +12% success chance (bosses excepted).',
      'APOTHEOSIS: missions it joins gain +20% success chance (bosses excepted).',
    ],
  },
  water: {
    id: 'water',
    element: 'water',
    name: 'Current Rider',
    icon: '💧',
    image: '/ui/passives/water.webp',
    unlockLevels: [5, 7, 9],
    tiers: [
      'Travels 25% faster.',
      'Travels 50% faster.',
      'Travels three times as fast — and CARRIES its whole team at that pace.',
      'APOTHEOSIS: travels FIVE times as fast, carrying its whole team.',
    ],
  },
  grass: {
    id: 'grass',
    element: 'grass',
    name: 'Verdant Mending',
    icon: '🌿',
    image: '/ui/passives/grass.webp',
    unlockLevels: [5, 7, 9],
    tiers: [
      'Heals you 1 HP whenever its mission succeeds.',
      'Heals you 2 HP whenever its mission succeeds.',
      'Heals you 3 HP per success — and feeds itself +1 bonus XP.',
      'APOTHEOSIS: heals you 5 HP per success, and feeds itself +1 bonus XP.',
    ],
  },
  lightning: {
    id: 'lightning',
    element: 'lightning',
    name: 'Storm Tempo',
    icon: '⚡',
    image: '/ui/passives/lightning.webp',
    unlockLevels: [5, 7, 9],
    tiers: [
      'Grants +6 ⚔️ Fight while on a team (each point: −4% fight time).',
      'Grants +11 ⚔️ Fight while on a team.',
      'Grants +15 ⚔️ Fight while on a team (−60% alone).',
      'APOTHEOSIS: grants +20 ⚔️ Fight — the team cap (−70%) on its own.',
    ],
  },
  earth: {
    id: 'earth',
    element: 'earth',
    name: 'Stoneguard',
    icon: '🪨',
    image: '/ui/passives/earth.webp',
    unlockLevels: [5, 7, 9],
    // v4.16 (per Javi): the guard also softens EXPIRIES while it waits
    // at camp — dispatched or benched, it guards nothing.
    tiers: [
      'Failures on its missions — and missions that expire while it waits at camp — deal 1 less damage.',
      'Failures on its missions — and missions that expire while it waits at camp — deal 2 less damage.',
      'Failures on its missions — and missions that expire while it waits at camp — deal 3 less damage.',
      'APOTHEOSIS: failures on its missions — and missions that expire while it waits at camp — deal 6 less damage.',
    ],
  },
}
